/**
 * Reseller's own feature-flag settings page.
 *
 * This is a thin wrapper around ResellerFeaturesEditor, which is the
 * canonical editor used by both entry points:
 *   - Reseller self-view (this file): renders with superAdminMode=false
 *     and the reseller's own id.
 *   - Super-admin drill-in (/resellers/:id/settings/features): renders
 *     the same component with superAdminMode=true.
 *
 * The `resellerId` prop is used by the super-admin drill-in route's
 * wrapper to drive both entry points through one component tree (see
 * router.tsx). When present, the caller supplies the ID; otherwise we
 * use the current tenant's reseller.
 */
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-store';
import { ResellerFeaturesEditor } from './ResellerFeaturesEditor';

interface Props {
  /** If provided (super-admin drill-in), set overrides on this reseller. */
  resellerId?: string;
}

export function FeaturesReseller({ resellerId }: Props = {}) {
  const user = useAuth((s) => s.user);
  const tenant = useAuth((s) => s.tenant);
  if (!user) return <Navigate to="/login" replace />;

  const effectiveResellerId = resellerId ?? tenant?.resellerId ?? null;
  if (!effectiveResellerId) {
    return <Navigate to="/dashboard" replace />;
  }

  // Super-admin is editing on behalf of a reseller.
  const superAdminMode = Boolean(user.is_super_admin && resellerId);

  return (
    <>
      {/* Resellers kept looking here for per-account switches (Sam
          2026-08-06) — point them at the right place. */}
      <div className="mb-5 rounded-md border border-blue-200 bg-blue-50/60 px-4 py-3 text-sm text-blue-900">
        These switches are your <span className="font-semibold">reseller-level defaults</span> —
        they apply to all of your customer accounts. To turn features on or off
        for <span className="font-semibold">one specific account</span>, open{' '}
        <Link to="/customers" className="font-semibold underline underline-offset-2">
          Customers
        </Link>{' '}
        and press <span className="font-semibold">Manage</span> on that account —
        its Features card overrides these defaults.
      </div>
      <ResellerFeaturesEditor
        resellerId={effectiveResellerId}
        superAdminMode={superAdminMode}
      />
    </>
  );
}
